import type { AxiosError } from 'axios';

interface ImsApiErrorData {
  success?: false;
  error?: {
    code?: string;
    message?: string;
    details?: Record<string, string>;
  };
  correlationId?: string;
}

// ASP.NET ModelState / ProblemDetails format
interface ModelStateErrorData {
  title?: string;
  errors?: Record<string, string | string[]>;
  correlationId?: string;
}

type ApiErrorData = ImsApiErrorData & ModelStateErrorData;

export interface ParsedApiError {
  message: string;
  code?: string;
  correlationId?: string;
  /** Field-level validation errors keyed by camelCase field name */
  fieldErrors?: Record<string, string>;
}

/**
 * Extracts a user-readable error message and optional field-level errors from an
 * Axios error (or any thrown value). Use this in every catch block instead of the
 * raw `err.response?.data?.error?.message` pattern so all error shapes are handled
 * consistently.
 *
 * @param err       The caught error value
 * @param fallback  Message to show when no specific message can be extracted
 */
export function parseApiError(err: unknown, fallback = 'An unexpected error occurred. Please try again.'): ParsedApiError {
  const axiosErr = err as AxiosError<ApiErrorData>;
  const data = axiosErr?.response?.data;

  // Standard IMS error envelope: { error: { code, message, details } }
  if (data?.error?.message) {
    return {
      message: data.error.message,
      code: data.error.code,
      correlationId: data.correlationId,
      fieldErrors: data.error.details,
    };
  }

  // Flat { message } shape used by a few older endpoints
  const flatMessage = (data as { message?: unknown } | undefined)?.message;
  if (typeof flatMessage === 'string' && flatMessage) {
    return { message: flatMessage, correlationId: data?.correlationId };
  }

  // ASP.NET ModelState validation errors: { errors: { Field: ["msg"] }, title }
  if (data?.errors) {
    const fieldErrors: Record<string, string> = {};
    let firstMessage = '';
    for (const [field, messages] of Object.entries(data.errors)) {
      const msg = Array.isArray(messages) ? messages[0] : String(messages);
      // Normalise PascalCase field names to camelCase
      const camelField = field.charAt(0).toLowerCase() + field.slice(1);
      fieldErrors[camelField] = msg;
      if (!firstMessage) firstMessage = msg;
    }
    return {
      message: data.title || firstMessage || fallback,
      fieldErrors,
      correlationId: data.correlationId,
    };
  }

  // ProblemDetails title with no field errors
  if (data?.title) {
    return { message: data.title, correlationId: data.correlationId };
  }

  // Network / connection error (request was made but no response received)
  if (axiosErr?.request && !axiosErr.response) {
    return { message: 'Network error. Please check your connection and try again.' };
  }

  // HTTP error with no parseable body
  if (axiosErr?.response) {
    return {
      message: fallback,
      correlationId: (data as ImsApiErrorData)?.correlationId,
    };
  }

  return { message: fallback };
}

/**
 * The server-provided error message with the correlation ID appended for
 * support reports ("Name already exists (Ref: abc-123)"), or undefined when
 * the response carried no message. Designed for fallback chains:
 *
 *   setError(getServerErrorMessage(err) ?? t('customer.failedToSave'));
 */
export function getServerErrorMessage(err: unknown): string | undefined {
  const axiosErr = err as AxiosError<ApiErrorData>;
  const data = axiosErr?.response?.data;
  const message = data?.error?.message
    || (typeof (data as { message?: unknown } | undefined)?.message === 'string'
        ? (data as { message: string }).message
        : undefined);
  if (!message) return undefined;
  return data?.correlationId ? `${message} (Ref: ${data.correlationId})` : message;
}

/**
 * One-call display string: server message (with Ref suffix) when available,
 * otherwise the caller's (usually localized) fallback.
 */
export function getApiErrorMessage(err: unknown, fallback?: string): string {
  const parsed = fallback === undefined ? parseApiError(err) : parseApiError(err, fallback);
  return parsed.correlationId ? `${parsed.message} (Ref: ${parsed.correlationId})` : parsed.message;
}
